import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { CheckCircle, AlertCircle, Trophy } from "lucide-react";

interface SuccessfulProposal {
  id: string;
  rfp_id: string;
  supplier_company_name: string;
  initial_bid_amount: number;
  invitation_id: string;
  final_bid_amount?: number;
  rfps: {
    title: string;
    description: string;
    matter_name: string | null;
  };
  supplier_invitations?: {
    invite_token: string;
  };
}

const SupplierSuccessfulProposals = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<SuccessfulProposal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      fetchSuccessfulProposals();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchSuccessfulProposals = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("proposals")
        .select(`
          id,
          rfp_id,
          supplier_company_name,
          initial_bid_amount,
          invitation_id,
          rfps (
            title,
            description,
            matter_name
          )
        `)
        .eq("user_id", user.id)
        .eq("selection_status", "successful");

      if (error) throw error;

      // Get invite tokens for navigation
      const proposalIds = (data || []).map(p => p.invitation_id);
      const { data: invitations } = await supabase
        .from("supplier_invitations")
        .select("id, invite_token")
        .in("id", proposalIds);

      const inviteMap = new Map((invitations || []).map(i => [i.id, i.invite_token]));

      // Get the lowest confirmed bid for each proposal (final winning bid)
      const proposalIdsForBids = (data || []).map(p => p.id);
      const { data: bidsData } = await supabase
        .from("bids")
        .select("proposal_id, amount")
        .in("proposal_id", proposalIdsForBids)
        .eq("status", "confirmed")
        .order("amount", { ascending: true });

      // Create a map of proposal_id to lowest bid amount
      const lowestBidMap = new Map<string, number>();
      (bidsData || []).forEach(bid => {
        if (!lowestBidMap.has(bid.proposal_id)) {
          lowestBidMap.set(bid.proposal_id, bid.amount);
        }
      });

      const proposalsWithTokens = (data || []).map(p => ({
        ...p,
        supplier_invitations: { invite_token: inviteMap.get(p.invitation_id) || "" },
        final_bid_amount: lowestBidMap.get(p.id) || p.initial_bid_amount
      }));

      setProposals(proposalsWithTokens);
    } catch (err) {
      console.error("Error fetching successful proposals:", err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Trophy className="h-8 w-8 text-green-500" />
            Successful Proposals
          </h1>
          <p className="text-muted-foreground mt-2">Proposals where you were selected as the winning bidder</p>
        </div>

        {proposals.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No successful proposals yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {proposals.map((proposal) => (
              <Card key={proposal.id} className="hover:shadow-lg transition-shadow border-green-200 bg-green-50/30 flex flex-col">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{proposal.rfps?.matter_name || proposal.rfps?.title}</CardTitle>
                    <Badge className="bg-green-500 text-white">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Won
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 flex-1 flex flex-col">
                  <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                    {proposal.rfps?.description}
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Your Winning Bid</span>
                      <span className="font-semibold text-green-600">${(proposal.final_bid_amount || proposal.initial_bid_amount).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="mt-auto pt-4">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/supplier/rfp/${proposal.rfp_id}/view/${proposal.supplier_invitations?.invite_token}`)}
                      className="w-full"
                    >
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierSuccessfulProposals;
